import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../utils/api';

interface PlatformStats {
  totalCustomers: number;
  totalPropertyGroups: number;
  totalUsers: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
  totalRevenue: number;
  revenueByType: Array<{
    type: string;
    _sum: { amount: number };
    _count: number;
  }>;
  recentRevenue: Array<{
    id: string;
    type: string;
    amount: number;
    description: string;
    createdAt: string;
    customer?: {
      id: string;
      name: string;
      email: string;
      planName: string;
    };
  }>;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);

  const fetchPlatformStats = async () => {
    try {
      setLoading(true);
      const response = await api.get<PlatformStats>('/api/platform-admin/stats');
      setStats(response.data as PlatformStats);
      setError('');
    } catch (err: any) {
      console.error('Error fetching platform stats:', err);
      setError('Failed to load platform statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if authentication has loaded
    const checkAuth = () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      
      if (storedUser || storedToken || user) {
        setAuthLoading(false);
      } else {
        // If no stored data and no user, wait a bit longer
        setTimeout(() => setAuthLoading(false), 1000);
      }
    };

    checkAuth();
  }, [user]);

  useEffect(() => {
    if (user && user.role === 'PLATFORM_ADMIN') {
      fetchPlatformStats();
    }
  }, [user]);

  // Show loading while authentication is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not platform admin
  if (!user || user.role !== 'PLATFORM_ADMIN') {
    return <Navigate to="/admin/login" replace />;
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100); // Convert from cents
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading platform data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchPlatformStats}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-red-600 rounded-full mr-3">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold">BlockBoard Platform Admin</h1>
                <p className="text-gray-400 text-sm">Welcome back, {user.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">Platform Admin</span>
              <a
                href="/admin/customers"
                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition duration-200 text-white"
              >
                Manage Customers
              </a>
              <a
                href="/profile"
                className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded-lg text-sm font-medium transition duration-200 text-white"
              >
                My Profile
              </a>
              <button
                onClick={() => {
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.href = '/admin/login';
                }}
                className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg text-sm font-medium transition duration-200"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Platform Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Customers</p>
                <p className="text-2xl font-bold text-white">{stats?.totalCustomers || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Active admin accounts</p>
              </div>
              <div className="bg-blue-500/20 p-3 rounded-lg">
                <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Property Groups</p>
                <p className="text-2xl font-bold text-white">{stats?.totalPropertyGroups || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Total communities managed</p>
              </div>
              <div className="bg-green-500/20 p-3 rounded-lg">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Active Subscriptions</p>
                <p className="text-2xl font-bold text-white">{stats?.activeSubscriptions || 0}</p>
                <p className="text-xs text-gray-500 mt-1">Paying customers</p>
              </div>
              <div className="bg-cyan-400/20 p-3 rounded-lg">
                <svg className="w-6 h-6 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Monthly Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(stats?.monthlyRevenue || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Current month earnings</p>
              </div>
              <div className="bg-yellow-500/20 p-3 rounded-lg">
                <svg className="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Platform Management */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Platform Management</h2>
            <div className="space-y-3">
              <button className="w-full text-left p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">View All Property Groups</p>
                    <p className="text-sm text-gray-400">Manage property groups and settings</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              <button 
                onClick={() => window.location.href = '/admin/customers'}
                className="w-full text-left p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition duration-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Customer Management</p>
                    <p className="text-sm text-gray-400">View and manage admin customers</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>

              <button className="w-full text-left p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition duration-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Billing Overview</p>
                    <p className="text-sm text-gray-400">Monitor subscriptions and payments</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            </div>
          </div>

          {/* System Health */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">System Health</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span className="text-white">API Status</span>
                </div>
                <span className="text-green-400 text-sm">Operational</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3"></div>
                  <span className="text-white">Database</span>
                </div>
                <span className="text-green-400 text-sm">Operational</span>
              </div>

              <div className="flex items-center justify-between p-3 bg-yellow-500/10 border border-yellow-500 rounded-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full mr-3"></div>
                  <span className="text-white">Email Service</span>
                </div>
                <span className="text-yellow-400 text-sm">Limited</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity and Revenue Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          {/* Recent Revenue */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Recent Platform Revenue</h2>
            <div className="space-y-3">
              {stats?.recentRevenue && stats.recentRevenue.length > 0 ? (
                stats.recentRevenue.slice(0, 5).map((revenue) => (
                  <div key={revenue.id} className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        revenue.type === 'SUBSCRIPTION' ? 'bg-green-400' : 'bg-blue-400'
                      }`}></div>
                      <div>
                        <p className="text-white text-sm font-medium">{revenue.description}</p>
                        <p className="text-gray-400 text-xs">
                          {revenue.customer?.name || 'System'} • {new Date(revenue.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <span className="text-green-400 font-medium">{formatCurrency(revenue.amount)}</span>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 text-center py-8">
                  <p>No recent revenue to display.</p>
                </div>
              )}
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
            <h2 className="text-xl font-bold text-white mb-4">Revenue Breakdown</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                <div>
                  <p className="text-white font-medium">Total Platform Revenue</p>
                  <p className="text-gray-400 text-sm">All-time earnings</p>
                </div>
                <span className="text-green-400 text-xl font-bold">{formatCurrency(stats?.totalRevenue || 0)}</span>
              </div>
              
              {stats?.revenueByType && stats.revenueByType.length > 0 ? (
                stats.revenueByType.map((typeData) => (
                  <div key={typeData.type} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg">
                    <div className="flex items-center">
                      <div className={`w-3 h-3 rounded-full mr-3 ${
                        typeData.type === 'SUBSCRIPTION' ? 'bg-green-400' : 'bg-blue-400'
                      }`}></div>
                      <div>
                        <p className="text-white text-sm">{typeData.type === 'SUBSCRIPTION' ? 'Subscriptions' : 'Payment Fees'}</p>
                        <p className="text-gray-400 text-xs">{typeData._count} transactions</p>
                      </div>
                    </div>
                    <span className="text-white font-medium">{formatCurrency(typeData._sum.amount || 0)}</span>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 text-center py-4">
                  <p>No revenue data available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;